/* eslint-disable no-nested-ternary */
import { useReadFilesAsBase64 } from 'components/FilesSelector/hooks/useReadFilesAsBase64';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    UerAddWorkOrderFromXmlMutation,
    UerReplaceWorkOrderFromRivxMutation,
    UerReplaceWorkOrderFromXmlMutation,
    UerImportWorkOrderFromRivxMutation,
    UerImportWorkOrderFromXmlMutation,
} from 'gql/graphql';
import { WorkOrderVorTypeEnum } from 'gql/graphql';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
    addWorkOrderFromXmlMutation,
    replaceWorkOrderFromRivxMutation,
    replaceWorkOrderFromXmlMutation,
    uploadWorkOrderFromRivxMutation,
    uploadWorkOrderFromXmlMutation,
} from '../workOrderQueries';

interface IUseUploadWorkOrder {
    projectId?: string;
    contractId?: number;
    workOrderId?: number;
    format: WorkOrderVorTypeEnum;
    constructionTypeId?: number;
    contractSeasonId?: number;
    isReplace?: boolean;
}

export const useUploadWorkOrder = (props: IUseUploadWorkOrder) => {
    const { projectId, contractId, format, contractSeasonId, constructionTypeId, workOrderId, isReplace } = props;
    const isRivx = format === WorkOrderVorTypeEnum.Rivx;
    const dispatch = useDispatch();

    const [makeUploadWorkOrderReset] = useResetStoreData({ requestId: 'uploadWorkOrder' });
    const [files, setFiles] = useState<File[]>([]);
    const base64Files = useReadFilesAsBase64(files);

    const [isSubmitted, setIsSubmitted] = useState(false);

    const query: any = useMemo(() => {
        if (isReplace) {
            return isRivx ? replaceWorkOrderFromRivxMutation : replaceWorkOrderFromXmlMutation;
        }
        if (workOrderId) {
            return addWorkOrderFromXmlMutation;
        }

        return isRivx ? uploadWorkOrderFromRivxMutation : uploadWorkOrderFromXmlMutation;
    }, [isReplace, isRivx, workOrderId]);

    const [uploadStatementData, uploadStatementStatus, uploadStatementError, uploadStatementMakeRequest] = useRequest({
        requestId: 'uploadWorkOrder',
        query,
        withEffect: false,
    });

    const validation = useMemo(() => {
        if (isReplace) {
            return isRivx
                ? (uploadStatementData as UerReplaceWorkOrderFromRivxMutation)?.uerReplaceWorkOrderFromRivx?.validation
                : (uploadStatementData as UerReplaceWorkOrderFromXmlMutation)?.uerReplaceWorkOrderFromXml?.validation;
        }

        if (workOrderId) {
            return (uploadStatementData as UerAddWorkOrderFromXmlMutation)?.uerAddWorkOrderFromXml?.validation;
        }

        return isRivx
            ? (uploadStatementData as UerImportWorkOrderFromRivxMutation)?.uerImportWorkOrderFromRivx?.validation
            : (uploadStatementData as UerImportWorkOrderFromXmlMutation)?.uerImportWorkOrderFromXml?.validation;
    }, [isReplace, isRivx, uploadStatementData, workOrderId]);

    const data = useMemo(() => {
        if (isReplace) {
            return isRivx
                ? (uploadStatementData as UerReplaceWorkOrderFromRivxMutation)?.uerReplaceWorkOrderFromRivx
                : (uploadStatementData as UerReplaceWorkOrderFromXmlMutation)?.uerReplaceWorkOrderFromXml;
        }

        if (workOrderId) {
            return (uploadStatementData as UerAddWorkOrderFromXmlMutation)?.uerAddWorkOrderFromXml;
        }

        return isRivx
            ? (uploadStatementData as UerImportWorkOrderFromRivxMutation)?.uerImportWorkOrderFromRivx
            : (uploadStatementData as UerImportWorkOrderFromXmlMutation)?.uerImportWorkOrderFromXml;
    }, [isReplace, isRivx, uploadStatementData, workOrderId]);

    useEffect(
        () => () => {
            makeUploadWorkOrderReset(undefined);
        },
        [makeUploadWorkOrderReset],
    );

    useEffect(() => {
        if (projectId && contractId && base64Files?.length && files.length && !data?.data?.id && !isSubmitted) {
            if (isReplace) {
                if (isRivx) {
                    dispatch(uploadStatementMakeRequest({ workOrderId, fileContent: base64Files[0] }));
                } else {
                    dispatch(
                        uploadStatementMakeRequest({
                            filesContent: base64Files,
                            contractSeasonId,
                            constructionTypeId,
                            workOrderId,
                        }),
                    );
                }
            } else if (isRivx) {
                dispatch(uploadStatementMakeRequest({ contractId, fileContent: base64Files[0] }));
            } else {
                let params: any = {
                    filesContent: base64Files,
                    contractSeasonId,
                    constructionTypeId,
                };

                params = workOrderId ? { ...params, workOrderId } : { ...params, contractId };

                dispatch(uploadStatementMakeRequest(params));
            }
            setIsSubmitted(true);
        }
    }, [
        base64Files,
        constructionTypeId,
        contractId,
        contractSeasonId,
        data?.data?.id,
        dispatch,
        files.length,
        isReplace,
        isRivx,
        isSubmitted,
        projectId,
        uploadStatementMakeRequest,
        workOrderId,
    ]);

    const makeRequestHandler = useCallback((filesToProcess) => {
        setFiles(filesToProcess);
    }, []);

    const handleCloseModalError = useCallback(() => {
        setIsSubmitted(false);
        setFiles([]);
        makeUploadWorkOrderReset(undefined);
    }, [makeUploadWorkOrderReset]);

    const handleCreateReport = useCallback(() => {
        ExportCSVReport(validation);
    }, [validation]);

    return {
        isSubmitted,
        uploadStatementData: data,
        uploadStatementStatus,
        uploadStatementError,
        makeRequestHandler,
        handleCloseModalError,
        handleCreateReport,
        makeUploadWorkOrderReset,
    };
};
